import { Currency } from '../constants/currency';
import { getCountryShortFromCurrency } from '../constants/country';

export enum FormatType {
  CURRENCY = 'CURRENCY',
  DECIMAL = 'DECIMAL',
  DECIMAL_CURRENCY = 'DECIMAL_CURRENCY',
}

const asKr = (value?: number) => {
  if (!value) {
    return '-';
  }
  return new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency: 'SEK',
  }).format(value / 100);
};
export default asKr;

export class Money {
  amount: number; // in cents

  currency?: Currency; // default to SEK

  constructor(
    amount: number | Money | { amount: number; currency: Currency },
    currency?: Currency,
  ) {
    if (amount instanceof Money || typeof amount === 'object') {
      this.amount = amount.amount;
      this.currency = amount.currency;
      return;
    }
    this.amount = amount;
    this.currency = currency;
  }

  format(formatType: FormatType = FormatType.CURRENCY): string {
    const decimalValue = this.asDecimal();
    const isInteger = Number.isInteger(decimalValue);
    let PriceFormatter;

    switch (formatType) {
      case FormatType.CURRENCY:
        PriceFormatter = new Intl.NumberFormat(
          getCountryShortFromCurrency(this.currency || Currency.SEK),
          {
            style: 'currency',
            currency: this.currency || Currency.SEK,
            minimumFractionDigits: isInteger ? 0 : 2,
            maximumFractionDigits: isInteger ? 0 : 2,
          },
        );

        return PriceFormatter.format(this.asDecimal())
          .replace(/\u00A0/g, ' ')
          .replace('−', '-');

      case FormatType.DECIMAL:
        return new Intl.NumberFormat(getCountryShortFromCurrency(this.currency || Currency.SEK), {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(decimalValue);

      case FormatType.DECIMAL_CURRENCY:
        PriceFormatter = new Intl.NumberFormat(
          getCountryShortFromCurrency(this.currency || Currency.SEK),
          {
            style: 'currency',
            currency: this.currency || Currency.SEK,
            minimumFractionDigits: isInteger ? 2 : 2,
            maximumFractionDigits: isInteger ? 2 : 2,
          },
        );

        return PriceFormatter.format(this.asDecimal())
          .replace(/\u00A0/g, ' ')
          .replace('−', '-');

      default:
        return '';
    }
  }

  multiply(multiplier: number): Money {
    return new Money(this.amount * multiplier, this.currency);
  }

  applyPercentageDiscount(percentage = 0): Money {
    return this.multiply(1 - percentage / 100);
  }

  subtract(subtrahend: Money | number): Money {
    if (typeof subtrahend === 'number') {
      return new Money(this.amount - subtrahend, this.currency);
    }
    return new Money(this.amount - subtrahend.amount, this.currency || subtrahend.currency);
  }

  divide(divisor: number | Money): Money {
    if (typeof divisor === 'number') {
      return new Money(this.amount / divisor, this.currency);
    }
    return new Money(this.amount / divisor.amount, this.currency || divisor.currency);
  }

  add(addend: Money | number): Money {
    if (typeof addend === 'number') {
      return new Money(this.amount + addend, this.currency);
    }
    return new Money(this.amount + addend.amount, this.currency || addend.currency);
  }

  round(): Money {
    return new Money(Math.round(this.amount), this.currency);
  }

  getRounding(): Money {
    return new Money(Math.round(this.amount / 100) * 100 - this.amount, this.currency);
  }

  abs(): Money {
    return new Money(Math.abs(this.amount), this.currency);
  }

  asDecimal(): number {
    return Math.round(this.amount) / 100;
  }

  toJSON() {
    return {
      amount: this.amount,
      currency: this.currency,
    };
  }
}
